import React, { Fragment, useState, useEffect } from "react";
import { Dialog, Transition } from "@headlessui/react";
import AddButton, { DraftButton } from "../buttons/button";
import ModalCloseIcon from "../../assets/icons/modal-close-icon";
import { PlusIcon, TrashIcon, ChevronUpIcon, ChevronDownIcon } from "@heroicons/react/24/outline";

const INPUT_TYPE_OPTIONS = [
  { value: "text", label: "Text" },
  { value: "number", label: "Number" },
  { value: "date", label: "Date" },
  { value: "datetime", label: "Date & Time" },
  { value: "dropdown", label: "Dropdown" },
  { value: "checkbox", label: "Checkbox" },
];

const SQL_TYPE_TO_INPUT = {
  DATE: "date",
  TIMESTAMP: "datetime",
  TIMESTAMPTZ: "datetime",
  INTEGER: "number",
  BIGINT: "number",
  SMALLINT: "number",
  NUMERIC: "number",
  DECIMAL: "number",
  REAL: "number",
  BOOLEAN: "checkbox",
  VARCHAR: "text",
  TEXT: "text",
  CHAR: "text",
};

const FilterConfigModal = ({
  isOpen,
  onClose,
  onSave,
  parameters = [],
  initialFilters = [],
  initialName = "",
  initialDescription = "",
  initialIsShared = false,
  isEdit = false,
  loading = false,
}) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [isShared, setIsShared] = useState(false);
  const [filters, setFilters] = useState([]);

  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (isOpen && !initialized) {
      setName(initialName);
      setDescription(initialDescription);
      setIsShared(initialIsShared);

      if (initialFilters.length > 0) {
        setFilters(initialFilters.map((f, i) => ({ ...f, order: f.order ?? i })));
      } else {
        setFilters(
          parameters.map((param, idx) => ({
            param_name: param.name,
            label: param.name
              .replace(/^p_/, "")
              .replace(/_/g, " ")
              .replace(/\b\w/g, (c) => c.toUpperCase()),
            input_type: SQL_TYPE_TO_INPUT[param.type] || "text",
            required: param.default_value === null,
            default_value: param.default_value || "",
            options: null,
            order: idx,
          }))
        );
      }
      setInitialized(true);
    }
    if (!isOpen) {
      setInitialized(false);
    }
  }, [isOpen, initialized]);

  const updateFilter = (index, field, value) => {
    const updated = [...filters];
    updated[index] = { ...updated[index], [field]: value };
    setFilters(updated);
  };

  const moveFilter = (index, direction) => {
    const newFilters = [...filters];
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= newFilters.length) return;
    [newFilters[index], newFilters[targetIndex]] = [newFilters[targetIndex], newFilters[index]];
    newFilters.forEach((f, i) => (f.order = i));
    setFilters(newFilters);
  };

  const addOption = (filterIndex) => {
    const updated = [...filters];
    const current = updated[filterIndex].options || [];
    updated[filterIndex] = { ...updated[filterIndex], options: [...current, ""] };
    setFilters(updated);
  };

  const updateOption = (filterIndex, optionIndex, value) => {
    const updated = [...filters];
    const options = [...(updated[filterIndex].options || [])];
    options[optionIndex] = value;
    updated[filterIndex] = { ...updated[filterIndex], options };
    setFilters(updated);
  };

  const removeOption = (filterIndex, optionIndex) => {
    const updated = [...filters];
    const options = (updated[filterIndex].options || []).filter((_, i) => i !== optionIndex);
    updated[filterIndex] = { ...updated[filterIndex], options: options.length > 0 ? options : null };
    setFilters(updated);
  };

  const handleSave = () => {
    if (!name.trim()) return;
    onSave({
      name: name.trim(),
      description: description.trim(),
      is_shared: isShared,
      filters: filters.map((f, i) => ({ ...f, order: i })),
    });
  };

  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog as="div" className="fixed inset-0 z-50 overflow-y-auto" onClose={onClose}>
        <div className="flex min-h-screen items-center justify-center px-4">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-100"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-black opacity-60" />
          </Transition.Child>

          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4"
            enterTo="opacity-100 translate-y-0"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-4"
          >
            <div className="relative bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div
                className="px-6 py-4 flex items-center justify-between rounded-t-xl sticky top-0 z-10"
                style={{ backgroundColor: "#F4F6F9" }}
              >
                <label className="text-sm fw_600">
                  {isEdit ? "Update Function Report" : "Save as Function Report"}
                </label>
                <ModalCloseIcon className="cursor-pointer" onClick={onClose} />
              </div>

              <div className="p-6 space-y-4">
                {/* Report Name */}
                <div>
                  <label className="block text-xs fw_500 text-344054 mb-1">
                    Report Name <span className="text-F51D00">*</span>
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g., B2B GST Report"
                    className="w-full px-3 py-2 border border-D0D5DD rounded-md text-sm focus:outline-none focus:border-primary"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-xs fw_500 text-344054 mb-1">Description</label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Describe this report (optional)"
                    rows={2}
                    className="w-full px-3 py-2 border border-D0D5DD rounded-md text-sm focus:outline-none focus:border-primary resize-none"
                  />
                </div>

                {/* Filters Configuration */}
                <div>
                  <label className="block text-xs fw_500 text-344054 mb-2">
                    Filter Configuration
                  </label>
                  {filters.length === 0 ? (
                    <p className="text-xsm text-667085">No parameters found in this function</p>
                  ) : (
                    <div className="space-y-3">
                      {filters.map((filter, idx) => (
                        <div key={filter.param_name} className="border border-EAECF0 rounded-lg p-3">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs fw_600 text-344054">
                              {filter.param_name}
                            </span>
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => moveFilter(idx, -1)}
                                disabled={idx === 0}
                                className="text-667085 hover:text-344054 disabled:opacity-30"
                              >
                                <ChevronUpIcon className="w-3 h-3" />
                              </button>
                              <button
                                onClick={() => moveFilter(idx, 1)}
                                disabled={idx === filters.length - 1}
                                className="text-667085 hover:text-344054 disabled:opacity-30"
                              >
                                <ChevronDownIcon className="w-3 h-3" />
                              </button>
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-2">
                            {/* Label */}
                            <div>
                              <label className="block text-xsm text-667085 mb-0.5">Label</label>
                              <input
                                type="text"
                                value={filter.label}
                                onChange={(e) => updateFilter(idx, "label", e.target.value)}
                                className="w-full px-2 py-1.5 border border-D0D5DD rounded-md text-xs focus:outline-none focus:border-primary"
                              />
                            </div>

                            {/* Input Type */}
                            <div>
                              <label className="block text-xsm text-667085 mb-0.5">Input Type</label>
                              <select
                                value={filter.input_type}
                                onChange={(e) => updateFilter(idx, "input_type", e.target.value)}
                                className="w-full px-2 py-1.5 border border-D0D5DD rounded-md text-xs focus:outline-none focus:border-primary bg-white"
                              >
                                {INPUT_TYPE_OPTIONS.map((opt) => (
                                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                              </select>
                            </div>

                            {/* Default Value */}
                            <div>
                              <label className="block text-xsm text-667085 mb-0.5">Default Value</label>
                              <input
                                type="text"
                                value={filter.default_value || ""}
                                onChange={(e) => updateFilter(idx, "default_value", e.target.value)}
                                placeholder="NULL"
                                className="w-full px-2 py-1.5 border border-D0D5DD rounded-md text-xs focus:outline-none focus:border-primary"
                              />
                            </div>

                            {/* Required */}
                            <div className="flex items-end pb-1.5">
                              <label className="flex items-center gap-1.5 text-xs text-344054">
                                <input
                                  type="checkbox"
                                  checked={filter.required}
                                  onChange={(e) => updateFilter(idx, "required", e.target.checked)}
                                  className="w-3.5 h-3.5 text-primary border-D0D5DD rounded focus:ring-primary"
                                />
                                Required
                              </label>
                            </div>
                          </div>

                          {/* Dropdown Options */}
                          {filter.input_type === "dropdown" && (
                            <div className="mt-2">
                              <div className="flex items-center justify-between mb-1">
                                <label className="text-xsm text-667085">Options</label>
                                <button
                                  onClick={() => addOption(idx)}
                                  className="flex items-center gap-0.5 text-xsm text-primary hover:text-blue-700"
                                >
                                  <PlusIcon className="w-3 h-3" /> Add
                                </button>
                              </div>
                              {(filter.options || []).map((opt, optIdx) => (
                                <div key={optIdx} className="flex items-center gap-1 mb-1">
                                  <input
                                    type="text"
                                    value={opt}
                                    onChange={(e) => updateOption(idx, optIdx, e.target.value)}
                                    placeholder="Option value"
                                    className="flex-1 px-2 py-1 border border-D0D5DD rounded text-xs focus:outline-none focus:border-primary"
                                  />
                                  <button
                                    onClick={() => removeOption(idx, optIdx)}
                                    className="text-667085 hover:text-F94B4B"
                                  >
                                    <TrashIcon className="w-3 h-3" />
                                  </button>
                                </div>
                              ))}
                              {(!filter.options || filter.options.length === 0) && (
                                <p className="text-xsm text-667085">Click "Add" to add dropdown options</p>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Share */}
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="fnReportShared"
                    checked={isShared}
                    onChange={(e) => setIsShared(e.target.checked)}
                    className="w-4 h-4 text-primary border-D0D5DD rounded focus:ring-primary"
                  />
                  <label htmlFor="fnReportShared" className="text-xs text-344054">
                    Share with store members
                  </label>
                </div>
              </div>

              <div className="px-6 py-4 flex justify-end gap-3 border-t border-EAECF0 sticky bottom-0 bg-white">
                <DraftButton text="Cancel" onclkFn={onClose} />
                <AddButton
                  text={isEdit ? "Update" : "Save Report"}
                  prefixIcon={false}
                  onclkFn={handleSave}
                  isDisabled={!name.trim()}
                  loading={loading}
                />
              </div>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
};

export default FilterConfigModal;
